"use client";
import React from 'react';
import { motion } from 'framer-motion';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';

const items = [
    {
        title: 'B2B Lead Generation',
        desc: 'Build campaigns designed to generate relevant business enquiries rather than simply maximise impressions or clicks.',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="7" width="24" height="18" rx="3" stroke="currentColor" strokeWidth="2" />
                <path d="M4 9l12 9 12-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Account-Based Marketing',
        desc: 'Focus advertising on selected companies or high-value account segments instead of treating every potential customer equally.',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <rect x="5" y="18" width="8" height="9" rx="1.5" stroke="currentColor" strokeWidth="2" />
                <rect x="12" y="10" width="8" height="17" rx="1.5" stroke="currentColor" strokeWidth="2" />
                <rect x="19" y="14" width="8" height="13" rx="1.5" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
    },
    {
        title: 'Decision-Maker Targeting',
        desc: 'Reach professional audiences based on relevant job functions, seniority, industries and company characteristics.',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="12" r="6" stroke="currentColor" strokeWidth="2" />
                <path d="M6 27c0-6 4.5-10 10-10s10 4 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M23 9l1.5 1.5L27 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Sponsored Content',
        desc: 'Put useful, relevant content in front of audiences that matter to your sales and marketing pipeline.',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="6" width="24" height="20" rx="3" stroke="currentColor" strokeWidth="2" />
                <path d="M9 12h14M9 17h14M9 22h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
    },
    {
        title: 'LinkedIn Lead Gen Forms',
        desc: "Reduce friction between ad engagement and enquiry using LinkedIn's native lead-generation experience.",
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <rect x="5" y="4" width="22" height="24" rx="3" stroke="currentColor" strokeWidth="2" />
                <path d="M10 11h12M10 16h12M10 21h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="24" cy="24" r="5" fill="currentColor" stroke="none" />
                <path d="M22 24l1.4 1.4L26.2 22.5" stroke="#fbfdfd" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Retargeting',
        desc: 'Re-engage people who have previously interacted with your content, website or brand.',
        icon: (
            <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <path d="M6 16a10 10 0 1 1 3 7.1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M6 23v-6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
];

function ManageRow({ item, index }: { item: typeof items[0]; index: number }) {
    const num = String(index + 1).padStart(2, '0');
    return (
        <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (index % 3) * 0.09 }}
            className="group flex items-start gap-5 py-6"
            style={{ borderBottom: '1px solid rgba(8,4,0,0.08)' }}
        >
            <span
                className="shrink-0 select-none pt-0.5"
                style={{ fontFamily: CLASH, fontSize: 15, color: 'rgba(8,4,0,0.28)', letterSpacing: '0.02em' }}
            >
                {num}
            </span>
            <div
                className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-300"
                style={{ background: 'rgba(55,48,163,0.08)', border: '1px solid rgba(55,48,163,0.16)', color: PURPLE }}
            >
                {item.icon}
            </div>
            <div className="flex flex-col gap-1.5 min-w-0">
                <h3 className="text-lg font-bold leading-snug" style={{ fontFamily: CLASH, color: INK, letterSpacing: '0.01em' }}>
                    {item.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)' }}>
                    {item.desc}
                </p>
            </div>
        </motion.div>
    );
}

export default function LinkedInAdsWhatWeManage() {
    return (
        <section className="relative overflow-hidden pt-4 pb-20 md:pt-6 md:pb-28 px-6 md:px-16 lg:px-24" style={{ background: '#ffffff', borderBottomLeftRadius: '80px', borderBottomRightRadius: '80px' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.035]" style={{ backgroundImage: 'radial-gradient(circle, rgba(55,48,163,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }} />

            <div className="max-w-[1200px] mx-auto flex flex-col gap-12 relative z-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div className="flex flex-col gap-4">
                        <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="text-xs font-bold tracking-[0.2em] uppercase"
                            style={{ color: PURPLE, fontFamily: CLASH }}
                        >
                            What We Cover
                        </motion.span>
                        <motion.h2
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-3xl md:text-5xl font-bold tracking-tight leading-tight"
                            style={{ fontFamily: CLASH, color: INK }}
                        >
                            What We Manage
                        </motion.h2>
                    </div>
                    <span className="text-sm font-mono" style={{ color: 'rgba(8,4,0,0.35)', letterSpacing: '0.05em' }}>
                        6 areas we cover
                    </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-12 lg:gap-x-16">
                    <div className="flex flex-col">
                        {items.slice(0, 3).map((it, i) => (
                            <ManageRow key={it.title} item={it} index={i} />
                        ))}
                    </div>
                    <div className="flex flex-col">
                        {items.slice(3).map((it, i) => (
                            <ManageRow key={it.title} item={it} index={i + 3} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
